import { simulationService } from './simulationService';
import { SimulationProgress } from '../../types/simulation';
import { syntheticRideData } from './syntheticData';
import { getToken } from '../../components/authentication/functions/authentication_management';
import axios from 'axios';
import { getDefaultRequestTime } from '../../utils/dateTimeUtils';
import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';

/**
 * Test script for simulation service
 * Run this to test individual endpoints and the complete workflow
 */

// London coordinates for testing
const LONDON_CENTER_LAT = 51.5074;
const LONDON_CENTER_LNG = -0.1278;

export async function testSimulationService() {
  console.log('🚀 Starting Simulation Service Tests...\n');

  try {
    // Test 1: Individual endpoint testing
    console.log('📋 Test 1: Individual Endpoint Testing');
    await testIndividualEndpoints();

    // Test 2: Complete synthetic simulation
    console.log('\n📋 Test 2: Complete Synthetic Simulation');
    await testCompleteSyntheticSimulation();

    console.log('\n✅ All tests completed successfully!');

  } catch (error) {
    console.error('\n❌ Test failed:', error);
    throw error;
  }
}

async function testIndividualEndpoints() {
  console.log('  Testing individual endpoints...');

  try {
    // Test ride upload
    console.log('  - Testing ride upload...');
    const rideResult = await simulationService.testEndpoint('uploadRides', {
      rides: [
        {
          external_reference_id: 'test-ride-1',
          from_latitude: LONDON_CENTER_LAT + 0.001,
          from_longitude: LONDON_CENTER_LNG + 0.001,
          to_latitude: LONDON_CENTER_LAT - 0.001,
          to_longitude: LONDON_CENTER_LNG - 0.001,
          request_type: 'departure_fixed',
          passengers: [{ passenger_type: 'standard', processing_time: 30, probability: 1.0 }],
          request_time: '2024-01-15T08:00:00Z'
        }
      ],
      name: `test-rides-${Date.now()}`
    });
    console.log('    ✅ Ride upload successful, batch_id:', rideResult.batch_id);

    // Test time window calculation
    console.log('  - Testing time window calculation...');
    const timeWindowResult = await simulationService.testEndpoint('calculateTimeWindows', {
      timeWindow: {
        time_model_type: 'slope_and_intercept',
        settings: {
          slope: 2.0,
          intercept: 1200,
          window: 1200,
          max_time_minutes: 90,
          once_off_processing_time_seconds: 15,
          processing_time: [
            { seat_type: 'standard', processing_time_seconds: 30 },
            { seat_type: 'wheelchair', processing_time_seconds: 90 }
          ]
        },
        ride_settings: { rides_name: 'test-rides', batch_id: null }
      },
      name: `test-time-window-${Date.now()}`
    });
    console.log('    ✅ Time window calculation successful, batch_id:', timeWindowResult.time_window_batch_id);

    // Test fleet generation
    console.log('  - Testing fleet generation...');
    const fleetResult = await simulationService.testEndpoint('generateFleet', {
      fleet: {
        fleet_name: 'Test Fleet',
        vehicles: [
          {
            vehicle_id: 'test-vehicle-1',
            vehicle_type: 'standard',
            capacity: 4,
            start_location: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
            operating_hours: { start_time: '06:00:00', end_time: '22:00:00' },
            cost_per_km: 0.50,
            cost_per_hour: 25.00
          }
        ]
      },
      name: `test-fleet-${Date.now()}`
    });
    console.log('    ✅ Fleet generation successful, fleet_id:', fleetResult.fleet_id);

    // Test problem config creation
    console.log('  - Testing problem config creation...');
    const problemResult = await simulationService.testEndpoint('createProblem', {
      config_name: 'test-config',
      iteration_count: 100,
      travelTimeMultiplier: 1.2
    });
    console.log('    ✅ Problem config creation successful, problem_id:', problemResult.problem_id);

    console.log('  ✅ All individual endpoint tests passed!');

  } catch (error) {
    console.error('  ❌ Individual endpoint test failed:', error);
    throw error;
  }
}

async function testCompleteSyntheticSimulation() {
  console.log('  Testing complete synthetic simulation...');

  try {
    const onProgressUpdate = (progress: SimulationProgress) => {
      console.log(`    Progress: ${progress.status} - ${progress.message || 'No message'}`);
      if (progress.progress !== undefined) {
        console.log(`    Progress: ${progress.progress}%`);
      }
    };

    const results = await simulationService.runSyntheticSimulation(onProgressUpdate);

    console.log('    ✅ Synthetic simulation completed successfully!');
    console.log('    Results summary:', {
      results_id: results.results_id,
      status: results.status,
      assigned_rides: results.summary.assigned_rides,
      unassigned_rides: results.summary.unassigned_rides,
      total_rides: results.summary.total_rides
    });

  } catch (error) {
    console.error('  ❌ Complete synthetic simulation test failed:', error);
    throw error;
  }
}

// Make functions available globally for console testing
if (typeof window !== 'undefined') {
  // Browser environment - expose for testing
  (window as any).testSimulationService = testSimulationService;
  (window as any).simulationService = simulationService;
  
  // Add some helper functions for easy testing
  (window as any).testRideUpload = async () => {
    console.log('🚀 Testing ride upload...');
    try {
      const result = await simulationService.testEndpoint('uploadRides', {
        rides: [
          {
            external_reference_id: 'console-test-ride',
            from_latitude: LONDON_CENTER_LAT + 0.001,
            from_longitude: LONDON_CENTER_LNG + 0.001,
            to_latitude: LONDON_CENTER_LAT - 0.001,
            to_longitude: LONDON_CENTER_LNG - 0.001,
            request_type: 'departure_fixed',
            passengers: [{ passenger_type: 'standard', processing_time: 30, probability: 1.0 }],
            request_time: '2024-01-15T08:00:00Z'
          }
        ],
        name: `console-test-rides-${Date.now()}`
      });
      console.log('✅ Ride upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Ride upload failed:', error);
      throw error;
    }
  };

  (window as any).testSyntheticSimulation = async () => {
    console.log('🚀 Testing complete synthetic simulation...');
    try {
      const results = await simulationService.runSyntheticSimulation((progress) => {
        console.log('📊 Progress:', progress);
      });
      console.log('✅ Synthetic simulation completed:', results);
      return results;
    } catch (error) {
      console.error('❌ Synthetic simulation failed:', error);
      throw error;
    }
  };

  (window as any).testDirectRideUpload = async () => {
    console.log('🚀 Testing direct ride upload (bypassing proxy)...');
    try {
      const token = localStorage.getItem('atlas_authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${getSimulationApiBaseUrl()}/data_generator/import_rides/test-direct-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify([
          {
            external_reference_id: 'direct-test-ride',
            from_latitude: LONDON_CENTER_LAT + 0.001,
            from_longitude: LONDON_CENTER_LNG + 0.001,
            to_latitude: LONDON_CENTER_LAT - 0.001,
            to_longitude: LONDON_CENTER_LNG - 0.001,
            request_type: 'departure_fixed',
            passengers: [{ passenger_type: 'standard', processing_time: 30, probability: 1.0 }],
            request_time: '2024-01-15T08:00:00Z'
          }
        ]),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Direct ride upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Direct ride upload failed:', error);
      throw error;
    }
  };

  (window as any).testPythonStyleUpload = async () => {
    console.log('🚀 Testing Python-style ride upload...');
    try {
      const token = localStorage.getItem('atlas_authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Generate proper UUIDs (like the Python script does)
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : ((r & 0x3) | 0x8);
          return v.toString(16);
        });
      };

      // Format data exactly like the Python script does
      const rideData = [
        {
          external_reference_id: 'python-style-test-ride',
          external_user_reference_id: 'test-user-1',
          from_latitude: LONDON_CENTER_LAT + 0.001,
          from_longitude: LONDON_CENTER_LNG + 0.001,
          to_latitude: LONDON_CENTER_LAT - 0.001,
          to_longitude: LONDON_CENTER_LNG - 0.001,
          request_type: 'departure_fixed',
          passengers: [
            {
              passenger_type: 'standard',
              processing_time: 30,
              probability: 1.0
            }
          ],
          request_time: '2024-01-15T08:00:00Z'.replace('Z', ''), // Remove Z like Python does
          ride_id: generateUUID(), // Proper UUID
          batch_id: generateUUID()  // Proper UUID
        }
      ];

      console.log('📊 Ride data formatted:', rideData);

      const response = await fetch(`${getSimulationApiBaseUrl()}/data_generator/import_rides/python-style-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(rideData),
      });

      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📊 Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Python-style ride upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Python-style ride upload failed:', error);
      throw error;
    }
  };

  (window as any).testFleetUpload = async () => {
    console.log('🚀 Testing fleet upload...');
    try {
      const result = await simulationService.testEndpoint('generateFleet', {
        fleet: {
          vehicles: [
            {
              number_of_vehicles: 1,
              vehicle: {
                capacity: [
                  { type: "standard", number: 4 },
                  { type: "wheelchair", number: 0 }
                ],
                brand: "test_brand",
                vehicle_type: "standard",
                shifts: [
                  {
                    start_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                    end_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                    start_time: "2024-01-15T06:00:00.000",
                    end_time: "2024-01-15T22:00:00.000"
                  }
                ]
              }
            }
          ]
        },
        name: `console-test-fleet-${Date.now()}`
      });
      console.log('✅ Fleet upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Fleet upload failed:', error);
      throw error;
    }
  };

  (window as any).testTimeWindowsCalculation = async () => {
    console.log('🚀 Testing time windows calculation...');
    try {
      const result = await simulationService.testEndpoint('calculateTimeWindows', {
        timeWindow: {
          time_model_type: 'slope_and_intercept',
          settings: {
            slope: 2.0,
            intercept: 1200,
            window: 1200,
            max_time_minutes: 90,
            once_off_processing_time_seconds: 15,
            processing_time: [
              { seat_type: 'standard', processing_time_seconds: 30 },
              { seat_type: 'wheelchair', processing_time_seconds: 90 }
            ]
          },
          ride_settings: { rides_name: 'test-rides', batch_id: null }
        },
        name: `console-test-time-window-${Date.now()}`
      });
      console.log('✅ Time windows calculation successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Time windows calculation failed:', error);
      throw error;
    }
  };

  (window as any).testProblemConfigCreation = async () => {
    console.log('🚀 Testing problem config creation...');
    try {
      // Generate proper UUID for problem id
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : ((r & 0x3) | 0x8);
          return v.toString(16);
        });
      };

      const result = await simulationService.testEndpoint('createProblem', {
        id: generateUUID(), // Required problem id
        configuration: {
          config_name: 'console-test-config',
          iteration_count: 100,
          travelTimeMultiplier: 1.2
        }
      });
      console.log('✅ Problem config creation successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Problem config creation failed:', error);
      throw error;
    }
  };

  (window as any).testScenarioCreation = async () => {
    console.log('🚀 Testing scenario creation...');
    try {
      // First create the required components
      const rideResult = await (window as any).testRideUpload();
      const fleetResult = await (window as any).testFleetUpload();
      const timeWindowResult = await (window as any).testTimeWindowsCalculation();
      const problemResult = await (window as any).testProblemConfigCreation();

      // Then create the scenario with simple ID fields
      const result = await simulationService.testEndpoint('createScenario', {
        scenario_name: `console-test-scenario-${Date.now()}`,
        ride_batch_id: rideResult.batch_id,
        fleet_id: fleetResult.fleet_id,
        time_window_batch_id: timeWindowResult.time_window_batch_id,
        problem_config_id: problemResult.problem_id
      });
      console.log('✅ Scenario creation successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Scenario creation failed:', error);
      throw error;
    }
  };

  (window as any).testSimulationRun = async () => {
    console.log('🚀 Testing simulation run...');
    try {
      // First create a scenario
      const scenarioResult = await (window as any).testScenarioCreation();
      
      // Then run the simulation
      const { runSimulation } = await import('./api');
      const { pashaRunSettingsFromRunBulk } = await import('./clusteringDefaults');
      const result = await runSimulation(
        scenarioResult.scenario_id,
        'pasha_bulka',
        pashaRunSettingsFromRunBulk(null)
      );
      console.log('✅ Simulation run successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Simulation run failed:', error);
      throw error;
    }
  };

  (window as any).testTaskStatusPolling = async () => {
    console.log('🚀 Testing task status polling...');
    try {
      // First run a simulation to get a task_id
      const simulationResult = await (window as any).testSimulationRun();
      
      // Then poll for status
      const { getTaskStatus } = await import('./api');
      const result = await getTaskStatus(
        simulationResult.task_id,
      );
      console.log('✅ Task status polling successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Task status polling failed:', error);
      throw error;
    }
  };

  (window as any).testSolutionRetrieval = async () => {
    console.log('🚀 Testing solution retrieval...');
    try {
      // First run a simulation and wait for completion
      const simulationResult = await (window as any).testSimulationRun();
      
      // Poll until completion
      const { getTaskStatus } = await import('./api');
      let status;
      let attempts = 0;
      const maxAttempts = 30; // 5 minutes with 10-second intervals
      
      while (attempts < maxAttempts) {
        status = await getTaskStatus(
          simulationResult.task_id,
        );
        
        console.log(`Polling attempt ${attempts + 1}:`, status);
        
        // Check for different possible status field names
        const currentStatus = status.status || status.task_status || status.state;
        
        if (currentStatus === 'COMPLETED') {
          break;
        } else if (currentStatus === 'FAILED') {
          throw new Error('Simulation failed');
        }
        
        console.log(`Status: ${currentStatus}`);
        await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
        attempts++;
      }
      
      const finalStatus = status.status || status.task_status || status.state;
      if (finalStatus !== 'COMPLETED') {
        throw new Error('Simulation timed out');
      }
      
      // Retrieve the solution
      const { getSolution } = await import('./api');
      const result = await getSolution(
        status.results_id,
      );
      console.log('✅ Solution retrieval successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Solution retrieval failed:', error);
      throw error;
    }
  };

  (window as any).testDirectFleetUpload = async () => {
    console.log('🚀 Testing direct fleet upload (bypassing proxy)...');
    try {
      const token = localStorage.getItem('atlas_authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Format fleet data according to backend FleetSettings schema
      const fleetData = {
        vehicles: [
          {
            number_of_vehicles: 1,
            vehicle: {
              capacity: [
                { type: "standard", number: 4 },
                { type: "wheelchair", number: 0 }
              ],
              brand: "test_brand",
              vehicle_type: "standard",
              shifts: [
                {
                  start_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                  end_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                  start_time: "2024-01-15T06:00:00.000",
                  end_time: "2024-01-15T22:00:00.000"
                }
              ]
            }
          },
          {
            number_of_vehicles: 1,
            vehicle: {
              capacity: [
                { type: "standard", number: 2 },
                { type: "wheelchair", number: 1 }
              ],
              brand: "test_brand",
              vehicle_type: "wheelchair",
              shifts: [
                {
                  start_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                  end_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
                  start_time: "2024-01-15T06:00:00.000",
                  end_time: "2024-01-15T22:00:00.000"
                }
              ]
            }
          }
        ]
      };

      console.log('📊 Fleet data formatted:', fleetData);

      const response = await fetch(`${getSimulationApiBaseUrl()}/data_generator/generate_fleet/direct-test-fleet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(fleetData),
      });

      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📊 Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Direct fleet upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Direct fleet upload failed:', error);
      throw error;
    }
  };

  (window as any).debugRideUpload = async () => {
    console.log('🔍 Debugging ride upload...');
    try {
      const tokenObj = getToken();
      console.log('📊 Token object:', tokenObj);
      
      if (!tokenObj) {
        throw new Error('User not authenticated');
      }

      console.log('📊 Access token:', tokenObj.access_token);
      console.log('📊 Token type:', tokenObj.token_type);

      // Use the exact synthetic data
      const rideData = syntheticRideData.rides;
      console.log('📊 Original ride data:', JSON.stringify(rideData, null, 2));

      // Format rides data like the API does
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : ((r & 0x3) | 0x8);
          return v.toString(16);
        });
      };

      const formattedRides = rideData.map((ride: any) => ({
        ...ride,
        ride_id: ride.ride_id || generateUUID(),
        batch_id: ride.batch_id || generateUUID(),
        request_time: ride.request_time ? ride.request_time.replace('Z', '') : getDefaultRequestTime()
      }));

      console.log('📊 Formatted ride data:', JSON.stringify(formattedRides, null, 2));

      // Use the exact same pattern as the working uploadRides function
      const apiBaseUrl = getSimulationApiBaseUrl();
      const ridesName = `debug-test-rides-${Date.now()}`;
      const url = `${apiBaseUrl}/data_generator/import_rides/${encodeURIComponent(ridesName)}`;
      
      console.log('📊 API Base URL:', apiBaseUrl);
      console.log('📊 Rides name:', ridesName);
      console.log('📊 Full URL:', url);

      const response = await axios.post(url, formattedRides, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenObj.access_token}`,
        },
      });

      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', response.headers);
      console.log('📊 Response data:', response.data);

      if (response.status !== 200) {
        console.error('📊 Error response:', response.data);
        throw new Error(`API error: ${response.status} - ${response.data}`);
      }

      console.log('✅ Debug ride upload successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Debug ride upload failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
      }
      throw error;
    }
  };

  (window as any).checkAuth = () => {
    console.log('🔍 Checking authentication status...');
    const tokenObj = getToken();
    console.log('📊 Token object:', tokenObj);
    
    if (tokenObj) {
      console.log('✅ User is authenticated');
      console.log('📊 Token type:', tokenObj.token_type);
      console.log('📊 Token length:', tokenObj.access_token?.length || 0);
      console.log('📊 Token preview:', tokenObj.access_token?.substring(0, 20) + '...');
    } else {
      console.log('❌ User is not authenticated');
      console.log('💡 Please log in first');
    }
    
    return tokenObj;
  };

  (window as any).checkTokenExpiration = () => {
    console.log('🔍 Checking token expiration...');
    const tokenObj = getToken();
    
    if (!tokenObj) {
      console.log('❌ No token found');
      return null;
    }
    
    try {
      // Decode the JWT token (without verification)
      const token = tokenObj.access_token;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      console.log('📊 Token payload:', payload);
      
      const now = Math.floor(Date.now() / 1000);
      const exp = payload.exp;
      
      console.log('📊 Current time (Unix):', now);
      console.log('📊 Token expiration (Unix):', exp);
      console.log('📊 Time until expiration:', exp - now, 'seconds');
      
      if (exp < now) {
        console.log('❌ Token has expired!');
        return false;
      } else {
        console.log('✅ Token is still valid');
        return true;
      }
    } catch (error) {
      console.error('❌ Error decoding token:', error);
      return null;
    }
  };

  (window as any).testSimpleAuth = async () => {
    console.log('🔍 Testing simple authentication...');
    try {
      const tokenObj = getToken();
      if (!tokenObj) {
        throw new Error('User not authenticated');
      }

      // Try a simple GET request to see if authentication works
      const apiBaseUrl = getSimulationApiBaseUrl();
      const response = await axios.get(`${apiBaseUrl}/tasks/`, {
        headers: {
          'Authorization': `Bearer ${tokenObj.access_token}`,
        },
      });

      console.log('✅ Simple auth test successful:', response.status);
      console.log('📊 Response data:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Simple auth test failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
      }
      throw error;
    }
  };

  (window as any).clearExpiredToken = () => {
    console.log('🧹 Clearing expired token...');
    localStorage.removeItem('atlas_authToken');
    console.log('✅ Token cleared from localStorage');
    console.log('💡 Please log in again to get a fresh token');
    console.log('🔄 You may need to refresh the page and re-authenticate');
  };

  (window as any).debugSyntheticData = async () => {
    console.log('🔍 Debugging synthetic data...');
    try {
      const { syntheticRideData, syntheticFleetData, syntheticTimeWindowData, syntheticProblemConfig } = await import('./syntheticData');
      
      console.log('📊 Synthetic Ride Data:', JSON.stringify(syntheticRideData, null, 2));
      console.log('📊 Synthetic Fleet Data:', JSON.stringify(syntheticFleetData, null, 2));
      console.log('📊 Synthetic Time Window Data:', JSON.stringify(syntheticTimeWindowData, null, 2));
      console.log('📊 Synthetic Problem Config:', JSON.stringify(syntheticProblemConfig, null, 2));
      
      // Test the first ride upload to see what's being sent
      console.log('🚀 Testing first ride upload with synthetic data...');
      const { uploadRides } = await import('./api');
      const token = localStorage.getItem('atlas_authToken');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      console.log('📊 Sending ride data:', JSON.stringify(syntheticRideData.rides, null, 2));
      
      const result = await uploadRides(
        syntheticRideData.rides,
        'debug-synthetic-rides',
      );
      
      console.log('✅ Debug ride upload successful:', result);
      return result;
    } catch (error: any) {
      console.error('❌ Debug synthetic data failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
        console.error('📊 Error response headers:', error.response.headers);
        
        // Show detailed validation errors
        if (error.response.data && error.response.data.detail) {
          console.error('📊 Validation errors:');
          error.response.data.detail.forEach((err: any, index: number) => {
            console.error(`  ${index + 1}. ${JSON.stringify(err, null, 2)}`);
          });
        }
      }
      throw error;
    }
  };

  (window as any).debugProblemConfig = async () => {
    console.log('🔍 Debugging problem config creation...');
    try {
      const { syntheticProblemConfig } = await import('./syntheticData');
      const { createProblem } = await import('./api');
      
      console.log('📊 Problem config data:', JSON.stringify(syntheticProblemConfig, null, 2));
      
      const result = await createProblem(
        syntheticProblemConfig,
      );
      
      console.log('✅ Problem config creation successful:', result);
      return result;
    } catch (error: any) {
      console.error('❌ Problem config creation failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
        console.error('📊 Error response headers:', error.response.headers);
      }
      throw error;
    }
  };

  (window as any).debugTaskStatus = async () => {
    console.log('🔍 Debugging task status API...');
    try {
      const { getTaskStatus } = await import('./api');
      
      // Use the task ID from the last simulation
      const taskId = '7f188892-cee8-4230-8c2d-ef9d3616e86e';
      console.log('📊 Checking status for task:', taskId);
      
      const result = await getTaskStatus(
        taskId,
      );
      
      console.log('📊 Raw API response:', result);
      console.log('📊 Response type:', typeof result);
      console.log('📊 Response keys:', Object.keys(result || {}));
      console.log('📊 Response structure:', JSON.stringify(result, null, 2));
      
      return result;
    } catch (error: any) {
      console.error('❌ Task status debug failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
      }
      throw error;
    }
  };

  (window as any).debugRidesAndTimeWindows = async () => {
    console.log('🔍 Debugging rides and time windows linkage...');
    try {
      const token = localStorage.getItem('atlas_authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use the IDs from the last simulation
      const rideBatchId = 'd324231f-f382-45ba-aeb7-fc6435988d47';
      const timeWindowBatchId = '817c5640-38a4-4bad-acfc-2cc46b70edcc';
      
      console.log('📊 Checking rides for batch_id:', rideBatchId);
      console.log('📊 Checking time windows for batch_id:', timeWindowBatchId);

      // Check rides
      const ridesResponse = await fetch(`${getSimulationApiBaseUrl()}/get_rides/?ride_batch_id=${rideBatchId}&time_window_batch_id=${timeWindowBatchId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('📊 Rides response status:', ridesResponse.status);
      
      if (!ridesResponse.ok) {
        const errorText = await ridesResponse.text();
        console.error('📊 Rides error response:', errorText);
        throw new Error(`HTTP ${ridesResponse.status}: ${errorText}`);
      }

      const ridesData = await ridesResponse.json();
      console.log('📊 Rides data:', ridesData);
      console.log('📊 Number of rides:', ridesData.length);
      
      if (ridesData.length === 0) {
        console.log('❌ No rides found! This explains the "list index out of range" error.');
        console.log('💡 The time windows calculation might not have completed or the rides/time windows are not properly linked.');
      } else {
        console.log('✅ Rides found successfully');
        console.log('📊 First ride sample:', ridesData[0]);
      }

      return ridesData;
    } catch (error: any) {
      console.error('❌ Debug rides and time windows failed:', error);
      if (error.response) {
        console.error('📊 Error response status:', error.response.status);
        console.error('📊 Error response data:', error.response.data);
      }
      throw error;
    }
  };

  // Debug logging (commented out to reduce console noise)
  // Uncomment for debugging:
  // console.log('🎯 Simulation testing functions loaded! Available commands:');
  // console.log('  - testSimulationService() - Run all tests');
  // console.log('  - testRideUpload() - Test just ride upload');
  // console.log('  - testFleetUpload() - Test fleet upload');
  // console.log('  - testTimeWindowsCalculation() - Test time windows calculation');
  // console.log('  - testProblemConfigCreation() - Test problem config creation');
  // console.log('  - testScenarioCreation() - Test scenario creation');
  // console.log('  - testSimulationRun() - Test simulation execution');
  // console.log('  - testTaskStatusPolling() - Test task status polling');
  // console.log('  - testSolutionRetrieval() - Test solution retrieval');
  // console.log('  - testSyntheticSimulation() - Test complete simulation');
  // console.log('  - testDirectRideUpload() - Test direct ride upload');
  // console.log('  - testPythonStyleUpload() - Test Python-style ride upload');
  // console.log('  - testDirectFleetUpload() - Test direct fleet upload');
  // console.log('  - debugRideUpload() - Test debug ride upload');
  // console.log('  - debugTaskStatus() - Debug task status API');
  // console.log('  - debugRidesAndTimeWindows() - Debug rides and time windows linkage');
  // console.log('  - simulationService - Access the main service');
} else {
  // Node.js environment
  console.log('Test script loaded. Call testSimulationService() to run tests.');
}
